// orders/orders.schema.ts
import { z } from "zod";
var B2BWaveOrderAddressSchema = z.object({
  address: z.string().nullable(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  country: z.string().nullable(),
  postal_code: z.string().nullable().optional(),
  name: z.string().optional(),
  comments: z.string().nullable().optional()
});
var B2BWaveOrderProductSchema = z.object({
  id: z.number(),
  order_id: z.number(),
  product_id: z.number(),
  quantity: z.string(),
  // B2BWave uses string for decimal quantities
  price: z.string(),
  // B2BWave uses string for monetary values
  final_price: z.string(),
  discount_percentage: z.string(),
  vat_percentage: z.string(),
  total_before_vat: z.string(),
  comments_customer: z.string().nullable().optional(),
  comments_wholesaler: z.string().nullable().optional(),
  total_vat: z.string(),
  total_after_vat: z.string(),
  option_value_ids: z.string().nullable().optional(),
  created_at: z.string().optional(),
  // Not present in list endpoint
  updated_at: z.string().optional(),
  // Not present in list endpoint
  option_values: z.record(z.unknown()).optional(),
  // Flexible object for option values
  product_variant_id: z.number().nullable().optional(),
  has_custom_price: z.boolean().optional().default(false),
  // Not present in list endpoint
  addressbook_id: z.number().nullable().optional(),
  // Additional fields from get order response
  status_order_id: z.number().nullable().optional(),
  status_order_name: z.string().optional(),
  product_variant_code: z.string().optional(),
  product_name: z.string().optional(),
  product_code: z.string().optional()
});
var B2BWaveOrderProductWrapperSchema = z.object({
  order_product: B2BWaveOrderProductSchema
});
var B2BWaveOrderSchema = z.object({
  id: z.coerce.number(),
  status_order_id: z.number(),
  customer_id: z.number(),
  invoice_no: z.string().nullable().optional(),
  customer_reference_no: z.string().nullable().optional(),
  comments_customer: z.string().nullable().optional(),
  comments_wholesaler: z.string().nullable().optional(),
  shipping_way: z.string().nullable().optional(),
  shipping_costs: z.string().optional().default("0"),
  date_estimated: z.string().nullable().optional(),
  total_before_discount: z.string().optional().default("0"),
  discount_percentage: z.string().optional().default("0"),
  discount_comments: z.string().nullable().optional(),
  discount_total: z.string().nullable().optional(),
  gross_total: z.string(),
  address: z.string().nullable(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  country: z.string().nullable(),
  postal_code: z.string().nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string(),
  addressbook_id: z.number().optional(),
  submitted_at: z.string().nullable().optional(),
  customer_order_reference: z.string().nullable().optional(),
  total_before_vat: z.string().optional().default("0"),
  total_after_vat: z.string().optional().default("0"),
  vat_group_id: z.number().nullable().optional(),
  total_products_before_discount: z.string().optional().default("0"),
  total_vat: z.string().optional().default("0"),
  total_products_before_vat: z.string().optional().default("0"),
  total_products_after_vat: z.string().optional().default("0"),
  total_shipping_after_vat: z.string().optional().default("0"),
  sales_rep_id: z.number().nullable().optional(),
  sales_rep_commission: z.string().optional().default("0"),
  extra_fields: z.record(z.unknown()).optional(),
  submitted_by: z.record(z.unknown()).optional(),
  submitted_by_class: z.string().nullable().optional(),
  integration_ref: z.string().nullable().optional(),
  payment_way_id: z.number().nullable().optional(),
  payment_ip: z.string().nullable().optional(),
  payment_token: z.string().nullable().optional(),
  payment_payer_id: z.string().nullable().optional(),
  payment_option_name: z.string().nullable().optional(),
  paid_amount: z.string().optional().default("0"),
  is_paid: z.boolean(),
  shipping_option_id: z.number().nullable().optional(),
  total_m3: z.string().optional().default("0"),
  request_delivery_at: z.string().nullable().optional(),
  pricelist_id: z.number().nullable().optional(),
  shipping_tracking: z.string().nullable().optional(),
  coupon_id: z.number().nullable().optional(),
  brightpearl_sales_order_id: z.number().nullable().optional(),
  cc_first_name: z.string().nullable().optional(),
  cc_last_name: z.string().nullable().optional(),
  cc_city: z.string().nullable().optional(),
  cc_province: z.string().nullable().optional(),
  cc_postal_code: z.string().nullable().optional(),
  cc_country: z.string().nullable().optional(),
  cc_address: z.string().nullable().optional(),
  total_quantity: z.string().optional().default("0"),
  payment_date: z.string().nullable().optional(),
  total_weight: z.string().optional().default("0"),
  // Additional fields from get order response
  status_order_name: z.string().optional(),
  customer_email: z.string().optional(),
  customer_company: z.string().optional(),
  customer_name: z.string().optional(),
  customer_pricelist_id: z.number().optional(),
  customer_phone: z.string().optional(),
  customer_reference_code: z.string().optional().nullable(),
  addressbook_reference_code: z.string().nullable().optional(),
  addressbook_type: z.string().optional(),
  addressbook_company_name: z.string().nullable().optional(),
  addressbook_name: z.string().nullable().optional(),
  addressbook_phone: z.string().nullable().optional(),
  currency: z.string().optional(),
  deleted_at: z.string().nullable().optional(),
  coupon_amount: z.string().optional(),
  coupon_vat: z.string().optional(),
  custom_discount_amount: z.string().optional()
});
var B2BWaveOrderResponseSchema = z.object({
  order: B2BWaveOrderSchema,
  order_products: z.array(B2BWaveOrderProductSchema)
});
var B2BWaveGetOrderResponseSchema = z.object({
  order: B2BWaveOrderSchema.extend({
    order_products: z.array(B2BWaveOrderProductWrapperSchema)
  })
});
var CreateB2BWaveOrderProductSchema = z.object({
  product_id: z.number().optional(),
  product_code: z.string().nullable().optional(),
  quantity: z.string(),
  addressbook_id: z.number().nullable().optional(),
  addressbook: B2BWaveOrderAddressSchema.nullable().optional()
}).refine((data) => data.product_id || data.product_code, {
  message: "Either product_id or product_code must be provided"
});
var CreateB2BWaveOrderSchema = z.object({
  customer_id: z.number().optional(),
  customer_email: z.string().email().optional(),
  addressbook_id: z.number().nullable().optional(),
  addressbook: B2BWaveOrderAddressSchema.nullable().optional(),
  customer_order_reference: z.string().nullable().optional(),
  comments_customer: z.string().optional(),
  order_products: z.array(CreateB2BWaveOrderProductSchema),
  payment_option_name: z.string().optional(),
  // Discount fields at top level (B2BWave API expects them here, not nested)
  discount_total: z.string().optional(),
  discount_comments: z.string().optional(),
  discount_percentage: z.string().optional()
}).refine((data) => data.customer_id || data.customer_email, {
  message: "Either customer_id or customer_email must be provided"
});
var GetB2BWaveOrdersQuerySchema = z.object({
  id_eq: z.number().optional(),
  status_order_id_eq: z.number().optional(),
  submitted_at_gteq: z.string().optional(),
  // YYYY-MM-DD format
  submitted_at_casted_eq: z.string().optional(),
  // YYYY-MM-DD format
  id_in: z.array(z.number()).optional(),
  status_order_id_in: z.array(z.number()).optional(),
  customer_id_eq: z.number().optional()
});
var B2BWaveOrdersListSchema = z.array(B2BWaveGetOrderResponseSchema);
var B2BWaveOrderAddressDtoSchema = z.object({
  address: z.string().nullable(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  country: z.string().nullable(),
  postalCode: z.string().nullable().optional(),
  name: z.string().optional(),
  comments: z.string().nullable().optional()
});
var B2BWaveOrderProductDtoSchema = z.object({
  id: z.number(),
  orderId: z.number(),
  productId: z.number(),
  quantity: z.string(),
  price: z.string(),
  finalPrice: z.string(),
  discountPercentage: z.string(),
  vatPercentage: z.string(),
  totalBeforeVat: z.string(),
  commentsCustomer: z.string().nullable().optional(),
  commentsWholesaler: z.string().nullable().optional(),
  totalVat: z.string(),
  totalAfterVat: z.string(),
  optionValueIds: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  optionValues: z.record(z.unknown()).optional(),
  productVariantId: z.number().nullable().optional(),
  hasCustomPrice: z.boolean().optional().default(false),
  addressbookId: z.number().nullable().optional(),
  statusOrderId: z.number().nullable().optional(),
  statusOrderName: z.string().optional(),
  productVariantCode: z.string().optional(),
  productName: z.string().optional(),
  productCode: z.string().optional()
});
var B2BWaveOrderProductWrapperDtoSchema = z.object({
  orderProduct: B2BWaveOrderProductDtoSchema
});
var B2BWaveOrderDtoSchema = z.object({
  id: z.number(),
  statusOrderId: z.number(),
  customerId: z.number(),
  invoiceNo: z.string().nullable().optional(),
  customerReferenceNo: z.string().nullable().optional(),
  commentsCustomer: z.string().nullable().optional(),
  commentsWholesaler: z.string().nullable().optional(),
  shippingWay: z.string().nullable().optional(),
  shippingCosts: z.string(),
  dateEstimated: z.string().nullable().optional(),
  totalBeforeDiscount: z.string(),
  discountPercentage: z.string(),
  discountComments: z.string().nullable().optional(),
  discountTotal: z.string().nullable().optional(),
  grossTotal: z.string(),
  address: z.string().nullable(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  country: z.string().nullable(),
  postalCode: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string(),
  addressbookId: z.number().optional(),
  submittedAt: z.string().nullable().optional(),
  customerOrderReference: z.string().nullable().optional(),
  totalBeforeVat: z.string(),
  totalAfterVat: z.string(),
  vatGroupId: z.number().nullable().optional(),
  totalProductsBeforeDiscount: z.string(),
  totalVat: z.string(),
  totalProductsBeforeVat: z.string(),
  totalProductsAfterVat: z.string(),
  totalShippingAfterVat: z.string(),
  salesRepId: z.number().nullable().optional(),
  salesRepCommission: z.string(),
  extraFields: z.record(z.unknown()).optional(),
  submittedBy: z.record(z.unknown()).optional(),
  submittedByClass: z.string().nullable().optional(),
  integrationRef: z.string().nullable().optional(),
  paymentWayId: z.number().nullable().optional(),
  paymentIp: z.string().nullable().optional(),
  paymentToken: z.string().nullable().optional(),
  paymentPayerId: z.string().nullable().optional(),
  paymentOptionName: z.string().nullable().optional(),
  paidAmount: z.string(),
  isPaid: z.boolean(),
  shippingOptionId: z.number().nullable().optional(),
  totalM3: z.string(),
  requestDeliveryAt: z.string().nullable().optional(),
  pricelistId: z.number().nullable().optional(),
  shippingTracking: z.string().nullable().optional(),
  couponId: z.number().nullable().optional(),
  brightpearlSalesOrderId: z.number().nullable().optional(),
  ccFirstName: z.string().nullable().optional(),
  ccLastName: z.string().nullable().optional(),
  ccCity: z.string().nullable().optional(),
  ccProvince: z.string().nullable().optional(),
  ccPostalCode: z.string().nullable().optional(),
  ccCountry: z.string().nullable().optional(),
  ccAddress: z.string().nullable().optional(),
  totalQuantity: z.string(),
  paymentDate: z.string().nullable().optional(),
  totalWeight: z.string(),
  statusOrderName: z.string().optional(),
  customerEmail: z.string().optional(),
  customerCompany: z.string().optional(),
  customerName: z.string().optional(),
  customerPricelistId: z.number().optional(),
  customerPhone: z.string().optional(),
  customerReferenceCode: z.string().optional(),
  addressbookReferenceCode: z.string().nullable().optional(),
  addressbookType: z.string().optional(),
  addressbookCompanyName: z.string().nullable().optional(),
  addressbookName: z.string().nullable().optional(),
  addressbookPhone: z.string().nullable().optional(),
  currency: z.string().optional(),
  deletedAt: z.string().nullable().optional(),
  couponAmount: z.string().optional(),
  couponVat: z.string().optional(),
  customDiscountAmount: z.string().optional()
});
var B2BWaveOrderResponseDtoSchema = z.object({
  order: B2BWaveOrderDtoSchema,
  orderProducts: z.array(B2BWaveOrderProductDtoSchema)
});
var B2BWaveGetOrderResponseDtoSchema = z.object({
  order: B2BWaveOrderDtoSchema.extend({
    orderProducts: z.array(B2BWaveOrderProductWrapperDtoSchema)
  })
});
var CreateB2BWaveOrderProductDtoSchema = z.object({
  productId: z.number().optional(),
  productCode: z.string().nullable().optional(),
  quantity: z.string(),
  addressbookId: z.number().nullable().optional(),
  addressbook: B2BWaveOrderAddressDtoSchema.nullable().optional()
}).refine((data) => data.productId || data.productCode, {
  message: "Either productId or productCode must be provided"
});
var CreateB2BWaveOrderDtoSchema = z.object({
  customerId: z.number().optional(),
  customerEmail: z.string().email().optional(),
  addressbookId: z.number().nullable().optional(),
  addressbook: B2BWaveOrderAddressDtoSchema.nullable().optional(),
  customerOrderReference: z.string().nullable().optional(),
  commentsCustomer: z.string().optional(),
  orderProducts: z.array(CreateB2BWaveOrderProductDtoSchema),
  paymentOptionName: z.string().optional(),
  // Discount fields at top level (B2BWave API expects them here, not nested)
  discountTotal: z.string().optional(),
  discountComments: z.string().optional(),
  discountPercentage: z.string().optional()
}).refine((data) => data.customerId || data.customerEmail, {
  message: "Either customerId or customerEmail must be provided"
});
var GetB2BWaveOrdersQueryDtoSchema = z.object({
  idEq: z.coerce.number().optional(),
  statusOrderIdEq: z.coerce.number().optional(),
  submittedAtGteq: z.string().optional(),
  submittedAtCastedEq: z.string().optional(),
  idIn: z.array(z.coerce.number()).optional(),
  statusOrderIdIn: z.array(z.coerce.number()).optional(),
  customerIdEq: z.coerce.number().optional()
});
var UpdateB2BWaveOrderInputDtoSchema = z.object({
  id: z.number(),
  commentsCustomer: z.string().optional(),
  commentsWholesaler: z.string().optional(),
  statusOrderId: z.number().optional(),
  customerOrderReference: z.string().optional()
});
var B2BWaveOrdersListDtoSchema = z.array(B2BWaveGetOrderResponseDtoSchema);
var GetB2BWaveOrderDocumentsQuerySchema = z.object({
  order_id: z.number()
});
var GetB2BWaveOrderDocumentsDTOQuerySchema = z.object({
  orderId: z.number()
});
var B2BWaveOrderDocumentSchema = z.object({
  id: z.number(),
  order_id: z.number(),
  file_name: z.string(),
  file_url: z.string(),
  created_at: z.string(),
  updated_at: z.string()
});
var B2BWaveOrderDocumentDTOSchema = z.object({
  id: z.number(),
  orderId: z.number(),
  fileName: z.string(),
  fileUrl: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
});
var B2BWaveOrderDocumentResponseSchema = z.array(B2BWaveOrderDocumentSchema);
var GetB2BWaveOrderDocumentsDTOResponseSchema = z.array(B2BWaveOrderDocumentDTOSchema);
var B2BWaveOrderWithProductsDtoSchema = B2BWaveOrderDtoSchema.extend({
  orderProducts: z.array(B2BWaveOrderProductDtoSchema)
});

// orders/local-order.schema.ts
import { z as z2 } from "zod";
var LocalOrderStatusSchema = z2.enum([
  "pending",
  "processing",
  "partially_fulfilled",
  "fulfilled",
  "shipped",
  "delivered",
  "cancelled"
]);
var LocalOrderPaymentMethodSchema = z2.enum(["ACH", "COD"]);
var OrderAddressSchema = z2.object({
  id: z2.string().uuid(),
  customerId: z2.string().nullable(),
  company: z2.string().nullable(),
  firstName: z2.string().nullable(),
  lastName: z2.string().nullable(),
  address1: z2.string().nullable(),
  address2: z2.string().nullable(),
  city: z2.string().nullable(),
  province: z2.string().nullable(),
  postalCode: z2.string().nullable(),
  countryCode: z2.string().nullable(),
  phone: z2.string().nullable(),
  deliveryLat: z2.number().nullable().optional(),
  deliveryLng: z2.number().nullable().optional(),
  metadata: z2.record(z2.unknown()).default({}),
  createdAt: z2.coerce.date(),
  updatedAt: z2.coerce.date(),
  deletedAt: z2.coerce.date().nullable().optional()
});
var CreateOrderAddressDtoSchema = z2.object({
  customerId: z2.string().optional(),
  company: z2.string().optional(),
  firstName: z2.string().optional(),
  lastName: z2.string().optional(),
  address1: z2.string().optional(),
  address2: z2.string().optional(),
  city: z2.string().optional(),
  province: z2.string().optional(),
  postalCode: z2.string().optional(),
  countryCode: z2.string().optional(),
  phone: z2.string().optional(),
  metadata: z2.record(z2.unknown()).optional()
});
var OrderLineItemSchema = z2.object({
  id: z2.string().uuid(),
  title: z2.string(),
  subtitle: z2.string().nullable(),
  thumbnail: z2.string().nullable(),
  variantId: z2.string().uuid().nullable(),
  productId: z2.string().uuid().nullable(),
  productTitle: z2.string().nullable(),
  productDescription: z2.string().nullable(),
  productHandle: z2.string().nullable(),
  variantSku: z2.string().nullable(),
  variantBarcode: z2.string().nullable(),
  variantTitle: z2.string().nullable(),
  unitPrice: z2.number(),
  compareAtUnitPrice: z2.number().nullable(),
  requiresShipping: z2.boolean().default(true),
  isDiscountable: z2.boolean().default(true),
  isTaxInclusive: z2.boolean().default(false),
  externalId: z2.string().nullable(),
  metadata: z2.record(z2.unknown()).default({}),
  createdAt: z2.coerce.date(),
  updatedAt: z2.coerce.date(),
  deletedAt: z2.coerce.date().nullable().optional()
});
var CreateOrderLineItemDtoSchema = z2.object({
  title: z2.string(),
  subtitle: z2.string().optional(),
  thumbnail: z2.string().optional(),
  variantId: z2.string().uuid().optional(),
  productId: z2.string().uuid().optional(),
  productTitle: z2.string().optional(),
  productDescription: z2.string().optional(),
  productHandle: z2.string().optional(),
  variantSku: z2.string().optional(),
  variantBarcode: z2.string().optional(),
  variantTitle: z2.string().optional(),
  unitPrice: z2.number(),
  compareAtUnitPrice: z2.number().optional(),
  requiresShipping: z2.boolean().optional(),
  isDiscountable: z2.boolean().optional(),
  isTaxInclusive: z2.boolean().optional(),
  externalId: z2.string().optional(),
  metadata: z2.record(z2.unknown()).optional()
});
var OrderItemSchema = z2.object({
  id: z2.string().uuid(),
  orderId: z2.string().uuid(),
  itemId: z2.string().uuid(),
  version: z2.number().int().default(1),
  quantity: z2.number().int().nonnegative(),
  fulfilledQuantity: z2.number().int().nonnegative().default(0),
  shippedQuantity: z2.number().int().nonnegative().default(0),
  deliveredQuantity: z2.number().int().nonnegative().default(0),
  returnRequestedQuantity: z2.number().int().nonnegative().default(0),
  returnReceivedQuantity: z2.number().int().nonnegative().default(0),
  writtenOffQuantity: z2.number().int().nonnegative().default(0),
  unitPrice: z2.number().nullable(),
  lineTotal: z2.number().nullable(),
  metadata: z2.record(z2.unknown()).default({}),
  createdAt: z2.coerce.date(),
  updatedAt: z2.coerce.date(),
  deletedAt: z2.coerce.date().nullable().optional()
});
var CreateOrderItemDtoSchema = z2.object({
  orderId: z2.string().uuid(),
  itemId: z2.string().uuid(),
  quantity: z2.number().int().positive(),
  unitPrice: z2.number().optional(),
  lineTotal: z2.number().optional(),
  metadata: z2.record(z2.unknown()).optional()
});
var OrderItemQuantitiesSchema = z2.object({
  fulfilledQuantity: z2.number().int().nonnegative().optional(),
  shippedQuantity: z2.number().int().nonnegative().optional(),
  deliveredQuantity: z2.number().int().nonnegative().optional(),
  returnRequestedQuantity: z2.number().int().nonnegative().optional(),
  returnReceivedQuantity: z2.number().int().nonnegative().optional(),
  writtenOffQuantity: z2.number().int().nonnegative().optional()
});
var OrderTotalsSchema = z2.object({
  subtotal: z2.number(),
  shippingTotal: z2.number(),
  taxTotal: z2.number(),
  discountTotal: z2.number(),
  total: z2.number(),
  itemCount: z2.number().int().nonnegative(),
  currency: z2.string().default("USD")
});
var OrderSummarySchema = z2.object({
  id: z2.string().uuid(),
  orderId: z2.string().uuid(),
  version: z2.number().int().default(1),
  totals: OrderTotalsSchema,
  createdAt: z2.coerce.date(),
  updatedAt: z2.coerce.date(),
  deletedAt: z2.coerce.date().nullable().optional()
});
var CreateOrderSummaryDtoSchema = z2.object({
  orderId: z2.string().uuid(),
  totals: OrderTotalsSchema
});
var OrderSchema = z2.object({
  id: z2.string().uuid(),
  displayId: z2.number().int(),
  version: z2.number().int().default(1),
  externalId: z2.string().nullable(),
  customerId: z2.number().int(),
  customerUuid: z2.string().uuid().nullable().optional(),
  email: z2.string().email().nullable(),
  status: LocalOrderStatusSchema.default("pending"),
  paymentMethod: LocalOrderPaymentMethodSchema,
  shippingAddressId: z2.string().uuid().nullable(),
  billingAddressId: z2.string().uuid().nullable(),
  currencyCode: z2.string().default("USD"),
  noNotification: z2.boolean().default(false),
  metadata: z2.record(z2.unknown()).default({}),
  placedAt: z2.coerce.date(),
  canceledAt: z2.coerce.date().nullable().optional(),
  createdAt: z2.coerce.date(),
  updatedAt: z2.coerce.date(),
  deletedAt: z2.coerce.date().nullable().optional()
});
var CreateOrderDtoSchema = z2.object({
  externalId: z2.string().optional(),
  customerId: z2.number().int(),
  customerUuid: z2.string().uuid().optional(),
  email: z2.string().email().optional(),
  paymentMethod: LocalOrderPaymentMethodSchema,
  shippingAddressId: z2.string().uuid().optional(),
  billingAddressId: z2.string().uuid().optional(),
  currencyCode: z2.string().optional(),
  noNotification: z2.boolean().optional(),
  metadata: z2.record(z2.unknown()).optional(),
  placedAt: z2.coerce.date()
});
var UpdateOrderStatusDtoSchema = z2.object({
  status: LocalOrderStatusSchema
});
var OrderWithItemsSchema = OrderSchema.extend({
  shippingAddress: OrderAddressSchema.nullable().optional(),
  billingAddress: OrderAddressSchema.nullable().optional(),
  items: z2.array(
    OrderItemSchema.extend({
      lineItem: OrderLineItemSchema
    })
  ).optional(),
  summary: OrderSummarySchema.nullable().optional()
});

// orders/b2bwave.ts
var B2BWaveOrderStatus = {
  /** Order submitted but not yet paid (initial state) */
  SUBMITTED: 2,
  /** ACH payment successfully completed. Ready to deliver */
  ACH_CLEARED_FOR_DELIVERY: 11,
  /** ACH payment failed/declined */
  ACH_FAILED: 19,
  /** ACH payment voided (cancelled after authorization) */
  ACH_VOIDED: 20,
  /** Cash on Delivery verified */
  COD_VERIFIED: 10,
  /** ACH Processing */
  ACH_PROCESSING: 21
};
var B2BWaveOrderStatusName = {
  [B2BWaveOrderStatus.SUBMITTED]: "1. Submitted",
  [B2BWaveOrderStatus.ACH_CLEARED_FOR_DELIVERY]: "ACH Cleared for Delivery",
  [B2BWaveOrderStatus.ACH_FAILED]: "ACH_FAILED",
  [B2BWaveOrderStatus.ACH_VOIDED]: "ACH_VOIDED",
  [B2BWaveOrderStatus.COD_VERIFIED]: "2. COD Verified",
  [B2BWaveOrderStatus.ACH_PROCESSING]: "ACH Processing"
};
var DELIVERABLE_ORDER_STATUSES = [
  B2BWaveOrderStatus.SUBMITTED,
  B2BWaveOrderStatus.ACH_CLEARED_FOR_DELIVERY,
  B2BWaveOrderStatus.ACH_PROCESSING
];

// orders/model.ts
var OrderStatus = /* @__PURE__ */ ((OrderStatus2) => {
  OrderStatus2[OrderStatus2["DRAFT"] = 1] = "DRAFT";
  OrderStatus2[OrderStatus2["PENDING"] = 2] = "PENDING";
  OrderStatus2[OrderStatus2["CONFIRMED"] = 3] = "CONFIRMED";
  OrderStatus2[OrderStatus2["PROCESSING"] = 4] = "PROCESSING";
  OrderStatus2[OrderStatus2["SHIPPED"] = 5] = "SHIPPED";
  OrderStatus2[OrderStatus2["DELIVERED"] = 6] = "DELIVERED";
  OrderStatus2[OrderStatus2["CANCELLED"] = 7] = "CANCELLED";
  OrderStatus2[OrderStatus2["RETURNED"] = 8] = "RETURNED";
  return OrderStatus2;
})(OrderStatus || {});
var OrderStatusNames = {
  [1 /* DRAFT */]: "Draft",
  [2 /* PENDING */]: "Pending",
  [3 /* CONFIRMED */]: "Confirmed",
  [4 /* PROCESSING */]: "Processing",
  [5 /* SHIPPED */]: "Shipped",
  [6 /* DELIVERED */]: "Delivered",
  [7 /* CANCELLED */]: "Cancelled",
  [8 /* RETURNED */]: "Returned"
};
export {
  B2BWaveGetOrderResponseDtoSchema,
  B2BWaveGetOrderResponseSchema,
  B2BWaveOrderAddressDtoSchema,
  B2BWaveOrderAddressSchema,
  B2BWaveOrderDocumentDTOSchema,
  B2BWaveOrderDocumentResponseSchema,
  B2BWaveOrderDocumentSchema,
  B2BWaveOrderDtoSchema,
  B2BWaveOrderProductDtoSchema,
  B2BWaveOrderProductSchema,
  B2BWaveOrderProductWrapperDtoSchema,
  B2BWaveOrderProductWrapperSchema,
  B2BWaveOrderResponseDtoSchema,
  B2BWaveOrderResponseSchema,
  B2BWaveOrderSchema,
  B2BWaveOrderStatus,
  B2BWaveOrderStatusName,
  B2BWaveOrderWithProductsDtoSchema,
  B2BWaveOrdersListDtoSchema,
  B2BWaveOrdersListSchema,
  CreateB2BWaveOrderDtoSchema,
  CreateB2BWaveOrderProductDtoSchema,
  CreateB2BWaveOrderProductSchema,
  CreateB2BWaveOrderSchema,
  CreateOrderAddressDtoSchema,
  CreateOrderDtoSchema,
  CreateOrderItemDtoSchema,
  CreateOrderLineItemDtoSchema,
  CreateOrderSummaryDtoSchema,
  DELIVERABLE_ORDER_STATUSES,
  GetB2BWaveOrderDocumentsDTOQuerySchema,
  GetB2BWaveOrderDocumentsDTOResponseSchema,
  GetB2BWaveOrderDocumentsQuerySchema,
  GetB2BWaveOrdersQueryDtoSchema,
  GetB2BWaveOrdersQuerySchema,
  LocalOrderPaymentMethodSchema,
  LocalOrderStatusSchema,
  OrderAddressSchema,
  OrderItemQuantitiesSchema,
  OrderItemSchema,
  OrderLineItemSchema,
  OrderSchema,
  OrderStatus,
  OrderStatusNames,
  OrderSummarySchema,
  OrderTotalsSchema,
  OrderWithItemsSchema,
  UpdateB2BWaveOrderInputDtoSchema,
  UpdateOrderStatusDtoSchema
};
